import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EquirectRenderer } from "../../renderers/gl/EquirectRenderer";

// Suppress ResizeObserver loop errors (they're harmless but noisy)
const suppressResizeObserverErrors = () => {
  const originalError = console.error;
  console.error = (...args) => {
    if (args[0]?.includes?.('ResizeObserver loop completed with undelivered notifications')) {
      return; // Suppress this specific error
    }
    originalError.apply(console, args);
  };
};

// Enhanced QuadView component with proper 360 rendering for each quadrant
export function QuadView({ video, baseView, presets, onPin, perf, quadZoom = 90, onQuadZoomChange }) {
  // Initialize views first before any useEffect that depends on it
  const views = useMemo(() => presets && presets.length >= 4 ? presets.slice(0, 4) : [
    { id: "front", label: "Front", yaw: 0, pitch: 0 },
    { id: "left", label: "Left", yaw: -90, pitch: 0 },
    { id: "right", label: "Right", yaw: 90, pitch: 0 },
    { id: "back", label: "Back", yaw: 180, pitch: 0 },
  ], [presets]);

  // Suppress ResizeObserver errors on component mount
  useEffect(() => {
    suppressResizeObserverErrors();
    
    // Debug: Log the view angles being used
    console.log('QuadView initialized with views:', views.map(v => `${v.label}: yaw=${v.yaw}°, pitch=${v.pitch}°`));
  }, [views]);
  
  const canvasRefs = [
    useRef(null), 
    useRef(null), 
    useRef(null), 
    useRef(null)
  ];
  
  const quadContainerRef = useRef(null);
  const renderersRef = useRef([]);

  // Wheel event handler for quad mode zoom
  const onWheel = useCallback((e) => {
    e.preventDefault();
    const newZoom = Math.max(30, Math.min(110, quadZoom + (e.deltaY > 0 ? 2 : -2)));
    onQuadZoomChange?.(newZoom);
  }, [quadZoom, onQuadZoomChange]);

  // Set up wheel event listener for zoom
  useEffect(() => {
    const container = quadContainerRef.current;
    if (!container) return;
    
    container.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      container.removeEventListener("wheel", onWheel);
    };
  }, [onWheel]);

  useEffect(() => {
    if (!video) return;
    
    // Initialize all renderers at once
    const renderers = canvasRefs.map((canvasRef, index) => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      
      try {
        const renderer = new EquirectRenderer(canvas, video);
        if (perf) {
          renderer.setPerf({
            ...perf,
            maxFps: Math.min(perf.maxFps || 30, 30)
          });
        }
        return renderer;
      } catch (error) {
        console.error('Failed to initialize renderer for quadrant', index, ':', error);
        return null;
      }
    });
    
    renderersRef.current = renderers;
    
    let stop = false;
    let lastTs = 0;
    const maxStep = 1000 / Math.min(perf?.maxFps ?? 30, 30);
    
    const renderFrame = (ts) => {
      if (stop) return;
      
      if (ts - lastTs < maxStep) {
        requestAnimationFrame(renderFrame);
        return;
      }
      
      lastTs = ts;
      
      // Render each quadrant with its specific view angle
      renderers.forEach((renderer, index) => {
        if (!renderer) return;
        
        try {
          const view = views[index];
          const yawRad = (view.yaw * Math.PI) / 180;
          const pitchRad = (view.pitch * Math.PI) / 180;
          const fovRad = (quadZoom * Math.PI) / 180;
          
          // Debug: Log rendering angles for first few frames
          if (ts < 5000 && index === 0) {
            console.log(`Rendering quadrant ${index} (${view.label}): yaw=${view.yaw}°, pitch=${view.pitch}°, fov=${quadZoom}°`);
          }
          
          renderer.render(yawRad, pitchRad, fovRad);
        } catch (error) {
          console.warn('Render error for quadrant', index, ':', error);
        }
      });
      
      requestAnimationFrame(renderFrame);
    };
    
    // Use requestVideoFrameCallback if available for smoother playback
    if (perf?.useRVFC && typeof video.requestVideoFrameCallback === 'function') {
      const videoCallback = (_, __) => {
        if (stop) return;
        
        renderers.forEach((renderer, index) => {
          if (!renderer) return;
          
          try {
            const view = views[index];
            const yawRad = (view.yaw * Math.PI) / 180;
            const pitchRad = (view.pitch * Math.PI) / 180;
            const fovRad = (quadZoom * Math.PI) / 180;
            
            renderer.render(yawRad, pitchRad, fovRad);
          } catch (error) {
            console.warn('Video callback render error for quadrant', index, ':', error);
          }
        });
        
        video.requestVideoFrameCallback(videoCallback);
      };
      
      video.requestVideoFrameCallback(videoCallback);
      
      return () => {
        stop = true;
        renderersRef.current = [];
      };
    }
    
    // Fallback to requestAnimationFrame
    requestAnimationFrame(renderFrame);
    
    return () => {
      stop = true;
      renderersRef.current = [];
    };
  }, [video, views, quadZoom, perf?.maxFps, perf?.useRVFC]);

  return (
      <>

      <div className="cam360-quad-grid" style={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        width: '100%',
        height: '100%',
        gap: '4px',
        background: '#000',
        boxSizing: 'border-box',
        overflow: 'hidden',
        padding: '4px',
        alignItems: 'stretch',
        justifyContent: 'center',
        borderRadius: '12px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.3), 0 1px 3px rgba(0,0,0,0.2)',
        border: '1px solid rgba(255,255,255,0.08)',
        margin: 0
      }}>
        {views.map((preset, index) => (
          <div 
            key={preset.id} 
            className="cam360-quad-item"
              style={{ 
                position: "relative", 
                background: "#000", 
                cursor: "pointer", 
                overflow: "hidden", 
                borderRadius: "8px", 
                minHeight: 0, 
                minWidth: 0, 
                display: "flex", 
                justifyContent: "center", 
                alignItems: "center",
                flex: "1 1 auto",
                width: "25%",
                height: "100%",
                border: "1px solid rgba(255,255,255,0.15)",
                transition: "all 0.3s ease",
                boxShadow: "0 2px 8px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)",
                margin: 0
              }}
            onClick={() => onPin?.(preset)}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(34,197,94,0.6)';
              e.currentTarget.style.transform = 'scale(1.02)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(34,197,94,0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
            }}
          >
              <canvas
                ref={canvasRefs[index]}
                className="cam360-canvas"
                style={{
                  width: "100%",
                  height: "100%",
                  display: "block",
                  imageRendering: "high-quality",
                  objectFit: "contain",
                  borderRadius: "8px"
                }}
              />
            <div 
              className="cam360-quad-label"
              style={{
                position: 'absolute',
                bottom: '6px',
                left: '6px',
                background: 'rgba(0,0,0,0.85)',
                color: 'white',
                padding: '6px 10px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: '600',
                pointerEvents: 'none',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255,255,255,0.15)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.4), 0 1px 2px rgba(0,0,0,0.2)'
              }}
            >
              {preset.label}
            </div>
            <div 
              className="cam360-quad-hover"
              style={{
                position: 'absolute',
                top: '0',
                left: '0',
                right: '0',
                bottom: '0',
                background: 'rgba(34,197,94,0.1)',
                opacity: '0',
                transition: 'opacity 0.3s ease',
                pointerEvents: 'none',
                borderRadius: '8px'
              }}
            />
          </div>
        ))}
      </div>
      <div 
        className="cam360-quad-zoom-hint"
        style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          background: 'rgba(0,0,0,0.85)',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '8px',
          fontSize: '11px',
          fontWeight: '600',
          pointerEvents: 'none',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.15)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.4), 0 1px 2px rgba(0,0,0,0.2)'
        }}
      >
        Scroll to zoom: {Math.round(quadZoom)}°
      </div>
      </>
  );
}
